import { FLOATING_ALERT_TIMING } from './constants/floating_alert_timing';
import { useEffect, useState } from 'react';
import styled, { css } from 'foundations-theming-specialty/utils';
import { useTranslation } from '../../hooks/useNavConfig';
import useTimeoutFn from '../../hooks/useTimeoutFn';
import CloseIcon from './icons/CloseIcon';
import { ExternalLinkIcon } from '../../nav-ui-components/Icons/ExternalLink';
import { ALERT_AUTO_DISMISS_TIMING_MS } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AlertContainer = styled.div.withConfig({
  displayName: "FloatingAlert__AlertContainer",
  componentId: "ax62we-0"
})(({
  theme,
  type
}) => {
  const alertTypeStyles = {
    warning: {
      backgroundColor: theme.color['fill-accent-red-subtle'],
      borderColor: theme.color['fill-accent-red-hover']
    },
    info: {
      backgroundColor: theme.color['fill-accent-blue-subtle'],
      borderColor: theme.color['fill-accent-blue-hover']
    }
  };
  return Object.assign({
    textAlign: 'left',
    display: 'inline-flex',
    maxWidth: '668px',
    boxShadow: theme.boxShadow['200'],
    pointerEvents: 'auto',
    borderWidth: theme.borderWidth['100'],
    borderStyle: 'solid',
    padding: `${theme.space['200']} ${theme.space['500']}`,
    justifyContent: `space-between`
  }, alertTypeStyles[type]);
});
const StyledContainer = styled.div.withConfig({
  displayName: "FloatingAlert__StyledContainer",
  componentId: "ax62we-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    display: 'flex',
    width: `calc(100% - ${theme.space['300']} - 20px)`,
    alignItems: 'center'
  };
});
const StyledTitle = styled.h5.withConfig({
  displayName: "FloatingAlert__StyledTitle",
  componentId: "ax62we-2"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({
    display: 'inline-flex',
    textAlign: 'left',
    margin: `0 ${theme.space['500']} 0 0`,
    color: theme.color['text-core-default']
  }, theme.typography['heading-100']);
});
const StyledBody = styled.p.withConfig({
  displayName: "FloatingAlert__StyledBody",
  componentId: "ax62we-3"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({
    display: 'inline-flex !important',
    textAlign: 'left',
    color: theme.color['text-core-default'],
    margin: 0
  }, theme.typography['body-100']);
});
const StyledLink = styled.a.withConfig({
  displayName: "FloatingAlert__StyledLink",
  componentId: "ax62we-4"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["text-decoration:underline !important;align-items:center;margin-left:", ";display:inline-flex !important;color:", ";", " & > svg{display:flex;margin-left:", ";fill:", " !important;width:13px !important;}"], theme.space['100'], theme.color['text-core-default'], theme.typography['body-100'], theme.space['100'], theme.color['text-core-default']);
});
const StyledButton = styled.button.withConfig({
  displayName: "FloatingAlert__StyledButton",
  componentId: "ax62we-5"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["width:", ";border:0;padding:0;background:transparent;cursor:pointer;z-index:1;display:flex;align-items:center;margin:0 0 0 ", ";& > svg{width:12px;}"], theme.space['300'], theme.space['800']);
});
const PopTransitionWrapper = styled.div.withConfig({
  displayName: "FloatingAlert__PopTransitionWrapper",
  componentId: "ax62we-6"
})(({
  theme: _theme,
  open
}) => {
  const theme = _theme;
  return css(["opacity:", ";transform:scale(", ");transition-property:opacity,transform;transition-duration:", ";pointer-events:", ";margin-top:", ";"], open ? '1' : '0', open ? '1' : '0.9', FLOATING_ALERT_TIMING, !open && 'none', theme.space['400']);
});
const FloatingAlert = ({
  content,
  visuallyHidden,
  onClose,
  type
}) => {
  // initially hidden to allow the alert to animate onto screen
  const {
    body,
    title,
    link
  } = content;
  const [isHidden, setIsHidden] = useState(true);
  const {
    t
  } = useTranslation();
  useTimeoutFn(onClose, ALERT_AUTO_DISMISS_TIMING_MS);
  useEffect(() => {
    setIsHidden(visuallyHidden);
  }, [visuallyHidden]);
  return /*#__PURE__*/_jsx(PopTransitionWrapper, {
    open: !isHidden,
    children: /*#__PURE__*/_jsxs(AlertContainer, {
      type: type,
      "aria-live": "polite",
      role: "alert",
      children: [/*#__PURE__*/_jsxs(StyledContainer, {
        children: [title ? /*#__PURE__*/_jsx(StyledTitle, {
          children: title
        }) : null, /*#__PURE__*/_jsxs(StyledBody, {
          children: [body, link ? /*#__PURE__*/_jsxs(StyledLink, {
            href: link.href,
            rel: "noreferrer",
            target: "_blank",
            children: [link.text, /*#__PURE__*/_jsx(ExternalLinkIcon, {})]
          }) : null]
        })]
      }), /*#__PURE__*/_jsx(StyledButton, {
        "data-test-id": "hs-nav-alert-close",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(CloseIcon, {
          title: t('nav.alerts.close')
        })
      })]
    })
  });
};
export default FloatingAlert;